import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/api';
import { Formik } from 'formik';
import * as Yup from 'yup';
import './ProfilePage.css';

const ProfilePage = () => {
  const { currentUser, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  // Profile validation schema
  const profileSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters'),
    certificationLevel: Yup.string()
      .required('Certification level is required')
      .oneOf(['OW', 'AOW', 'Rescue', 'DM', 'Instructor']),
    numberOfDives: Yup.number()
      .required('Number of dives is required')
      .min(0, 'Cannot be negative')
      .integer('Must be a whole number'),
    emergencyContactName: Yup.string(),
    emergencyContactRelationship: Yup.string(),
    emergencyContactPhone: Yup.string()
  });

  // Password validation schema
  const passwordSchema = Yup.object().shape({
    currentPassword: Yup.string()
      .required('Current password is required'),
    newPassword: Yup.string()
      .required('New password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
  });

  const handleProfileUpdate = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const profileData = {
        name: values.name,
        certificationLevel: values.certificationLevel,
        numberOfDives: values.numberOfDives,
        emergencyContact: {
          name: values.emergencyContactName,
          relationship: values.emergencyContactRelationship,
          phone: values.emergencyContactPhone
        }
      };

      const response = await userService.updateProfile(profileData);
      updateUser(response.data);
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handlePasswordChange = async (values, { setSubmitting, resetForm }) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await userService.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      setSuccess('Password changed successfully!');
      resetForm();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Please log in to view your profile.</Alert>
      </Container>
    );
  }

  return (
    <Container className="profile-page py-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <div className="profile-header mb-4">
            <div className="profile-avatar">
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2>{currentUser.name}</h2>
              <p className="text-muted mb-0">{currentUser.email}</p>
              {currentUser.role === 'admin' && (
                <span className="badge bg-primary mt-2">Admin</span>
              )}
            </div>
          </div>

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              <i className="bi bi-check-circle me-2"></i>
              {success}
            </Alert>
          )}

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
            {/* Profile Information Tab */}
            <Tab eventKey="profile" title="📋 Profile Information">
              <Card>
                <Card.Body>
                  <Formik
                    initialValues={{
                      name: currentUser.name || '',
                      certificationLevel: currentUser.certificationLevel || 'OW',
                      numberOfDives: currentUser.numberOfDives || 0,
                      emergencyContactName: currentUser.emergencyContact?.name || '',
                      emergencyContactRelationship: currentUser.emergencyContact?.relationship || '',
                      emergencyContactPhone: currentUser.emergencyContact?.phone || ''
                    }}
                    validationSchema={profileSchema}
                    onSubmit={handleProfileUpdate}
                  >
                    {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
                      <Form onSubmit={handleSubmit}>
                        <h5 className="mb-4">Personal Information</h5>
                        
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Full Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                isInvalid={touched.name && !!errors.name}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email Address</Form.Label>
                              <Form.Control
                                type="email"
                                value={currentUser.email}
                                disabled
                                className="bg-light"
                              />
                              <Form.Text className="text-muted">
                                Email cannot be changed
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <h5 className="mb-4 mt-4">Diving Information</h5>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Certification Level</Form.Label>
                              <Form.Select
                                name="certificationLevel"
                                value={values.certificationLevel}
                                onChange={handleChange}
                                isInvalid={touched.certificationLevel && !!errors.certificationLevel}
                              >
                                <option value="OW">Open Water (OW)</option>
                                <option value="AOW">Advanced Open Water (AOW)</option>
                                <option value="Rescue">Rescue Diver</option>
                                <option value="DM">Divemaster (DM)</option>
                                <option value="Instructor">Instructor</option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.certificationLevel}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Number of Dives</Form.Label>
                              <Form.Control
                                type="number"
                                name="numberOfDives"
                                value={values.numberOfDives}
                                onChange={handleChange}
                                min="0"
                                isInvalid={touched.numberOfDives && !!errors.numberOfDives}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.numberOfDives}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <h5 className="mb-4 mt-4">Emergency Contact</h5>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Contact Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="emergencyContactName"
                                value={values.emergencyContactName}
                                onChange={handleChange}
                                placeholder="e.g., John Smith"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Relationship</Form.Label>
                              <Form.Control
                                type="text"
                                name="emergencyContactRelationship"
                                value={values.emergencyContactRelationship}
                                onChange={handleChange}
                                placeholder="e.g., Spouse, Parent, Sibling"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number</Form.Label>
                              <Form.Control
                                type="tel"
                                name="emergencyContactPhone"
                                value={values.emergencyContactPhone}
                                onChange={handleChange}
                                placeholder="e.g., +49 123 456 7890"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-4">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting || loading}
                          >
                            {isSubmitting || loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>
                                Save Changes
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </Tab>

            {/* Security Tab */}
            <Tab eventKey="security" title="🔒 Security">
              <Card>
                <Card.Body>
                  <h5 className="mb-4">Change Password</h5>
                  
                  <Formik
                    initialValues={{
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    }}
                    validationSchema={passwordSchema}
                    onSubmit={handlePasswordChange}
                  >
                    {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Current Password</Form.Label>
                              <Form.Control
                                type="password"
                                name="currentPassword"
                                value={values.currentPassword}
                                onChange={handleChange}
                                isInvalid={touched.currentPassword && !!errors.currentPassword}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.currentPassword}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>New Password</Form.Label>
                              <Form.Control
                                type="password"
                                name="newPassword"
                                value={values.newPassword}
                                onChange={handleChange}
                                isInvalid={touched.newPassword && !!errors.newPassword}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.newPassword}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                Minimum 6 characters
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Confirm New Password</Form.Label>
                              <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={values.confirmPassword}
                                onChange={handleChange}
                                isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.confirmPassword}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Alert variant="info" className="mt-3">
                          <i className="bi bi-info-circle me-2"></i>
                          Make sure your password is strong and unique. Don't reuse passwords from other sites.
                        </Alert>

                        <div className="d-flex justify-content-end mt-4">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting || loading}
                          >
                            {isSubmitting || loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Changing Password...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-shield-lock me-2"></i>
                                Change Password
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </Tab>

            {/* Account Info Tab */}
            <Tab eventKey="account" title="ℹ️ Account Info">
              <Card>
                <Card.Body>
                  <h5 className="mb-4">Account Details</h5>

                  <div className="account-info-grid">
                    <div className="info-item">
                      <div className="info-label">Account Type</div>
                      <div className="info-value">
                        {currentUser.role === 'admin' ? (
                          <span className="badge bg-primary">Administrator</span>
                        ) : (
                          <span className="badge bg-secondary">Regular User</span>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">Email Verified</div>
                      <div className="info-value">
                        {currentUser.isVerified ? (
                          <span className="text-success">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            Verified
                          </span>
                        ) : (
                          <span className="text-danger">
                            <i className="bi bi-x-circle-fill me-1"></i>
                            Not Verified
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">Member Since</div>
                      <div className="info-value">
                        {new Date(currentUser.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">Last Updated</div>
                      <div className="info-value">
                        {currentUser.updatedAt ? 
                          new Date(currentUser.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                          : 'Never'
                        }
                      </div>
                    </div>

                    <div className="info-item">
                      <div className="info-label">User ID</div>
                      <div className="info-value">
                        <code>{currentUser._id}</code>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;
